import { Share2, Link2, Send, MessageCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// XIcon (Twitter) — inline since lucide has no official X logo
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" {...props}>
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.82 9.82 0 001.517 5.248l-.999 3.648 3.971-1.042zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
  </svg>
);

const TelegramIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" {...props}>
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

export default function ShareMenu({ postId, text = '', children, align = 'end', contentClassName = '', testId = 'share-menu' }) {
  // Public backend preview URL => rich embeds (M logo image, playable video) on X/WhatsApp/Telegram.
  const shareUrl = `${BACKEND}/api/share/${postId}`;
  const shareText = text ? (text.length > 120 ? text.slice(0, 117) + '…' : text) : 'Check this out on Midgard';

  const open = (href) => {
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const shareX = () =>
    open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`);
  const shareWhatsApp = () =>
    open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`);
  const shareTelegram = () =>
    open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`);
  const copyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Midgard', text: shareText, url: shareUrl });
      } catch (e) {
        if (e?.name !== 'AbortError') copyLink();
      }
    } else {
      copyLink();
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild data-testid={`${testId}-trigger`}>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className={`w-52 ${contentClassName}`} data-testid={`${testId}-content`}>
        <DropdownMenuItem onClick={shareX} data-testid="share-x" className="gap-2 cursor-pointer">
          <XIcon /> Share on X
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareWhatsApp} data-testid="share-whatsapp" className="gap-2 cursor-pointer">
          <WhatsAppIcon /> WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareTelegram} data-testid="share-telegram" className="gap-2 cursor-pointer">
          <TelegramIcon /> Telegram
        </DropdownMenuItem>
        <DropdownMenuItem onClick={copyLink} data-testid="share-copy" className="gap-2 cursor-pointer">
          <Link2 className="w-4 h-4" /> Copy link
        </DropdownMenuItem>
        {typeof navigator !== 'undefined' && navigator.share && (
          <DropdownMenuItem onClick={nativeShare} data-testid="share-native" className="gap-2 cursor-pointer">
            <Send className="w-4 h-4" /> More…
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
